/** GPU textures from the shared pixel-art atlas. Nearest filtering keeps sprite
 * texels crisp; perspective camera framing is owned by framing.ts.
 */

import * as THREE from 'three'
import { getSprite, renderAtlas } from '../engine/atlas'
import { tint } from '../engine/sprite'

/** Nearest-neighbour, no mipmaps, sRGB. Applied to every art texture. */
function configure(tex: THREE.Texture): THREE.Texture {
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.NearestFilter
  tex.generateMipmaps = false
  tex.colorSpace = THREE.SRGBColorSpace
  tex.needsUpdate = true
  return tex
}

function wrapClamp(tex: THREE.Texture): THREE.Texture {
  // Art is authored per-face, so a face sampling past its own edge must not
  // wrap around to the opposite side of the sprite.
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  return tex
}

const TEXTURES = new Map<string, THREE.Texture>()

/**
 * A texture for a sprite key, cached for the module lifetime. Unknown keys
 * resolve to a transparent pixel; registry tests catch missing production art.
 */
export function spriteTexture(key: string): THREE.Texture {
  const hit = TEXTURES.get(key)
  if (hit !== undefined) return hit

  const canvas = renderAtlas(getSprite(key) ?? { w: 1, h: 1, data: ['.'] })
  const tex = wrapClamp(configure(new THREE.CanvasTexture(canvas)))
  TEXTURES.set(key, tex)
  return tex
}

const TINTED = new Map<string, THREE.Texture>()

/**
 * A texture for a sprite recoloured through the palette. The tint is applied to
 * the sprite *data* rather than as a material colour, so a transparent pixel
 * stays transparent instead of gaining a coloured halo.
 */
export function tintedTexture(key: string, colour: string): THREE.Texture {
  const cacheKey = `${key}|${colour}`
  const hit = TINTED.get(cacheKey)
  if (hit !== undefined) return hit

  const sprite = getSprite(key)
  const tex =
    sprite === undefined
      ? spriteTexture(key)
      : wrapClamp(
          configure(
            new THREE.CanvasTexture(
              renderAtlas(tint(sprite, { '.': colour, K: colour })),
            ),
          ),
        )
  TINTED.set(cacheKey, tex)
  return tex
}
